import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

// Typed translation layer shared by the CMS and the super console. Each app passes its own English and
// Indonesian catalogs (English is the source of truth: a key missing from `id` is a type error) and gets a
// provider, hooks and a non-React `translate`.

export type Lang = 'en' | 'id'
export const LANGS: readonly Lang[] = ['en', 'id']
export const LANG_NAMES: Record<Lang, string> = { en: 'English', id: 'Bahasa Indonesia' }
/** BCP 47 tag used for dates and numbers. */
export const LANG_LOCALE: Record<Lang, string> = { en: 'en-GB', id: 'id-ID' }

export type Vars = Record<string, string | number>

export function createI18n<Key extends string>(catalogs: { en: Record<Key, string>; id: Record<Key, string> }, options: { storageKey: string }) {
  type T = (key: Key, vars?: Vars) => string

  function initialLang(): Lang {
    try {
      const stored = localStorage.getItem(options.storageKey)
      if (stored === 'en' || stored === 'id') return stored
    } catch {
      // Storage blocked: fall through to the browser language.
    }
    return typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('id') ? 'id' : 'en'
  }

  let current: Lang = initialLang()

  /** The active language outside React (formatters). Components re-render on change through useT(). */
  const getLang = () => current

  /**
   * `{name}` placeholders are replaced from `vars`. A message written as `one|other` (with a `count` var)
   * picks the first part when count is 1, otherwise the second: `'{count} item|{count} items'`.
   */
  function translate(key: Key, vars?: Vars, lang: Lang = current): string {
    let text = catalogs[lang][key] ?? catalogs.en[key] ?? key
    if (vars && 'count' in vars && text.includes('|')) {
      const [one = '', other = ''] = text.split('|')
      text = Number(vars['count']) === 1 ? one : other
    }
    return vars ? text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole)) : text
  }

  type Value = { lang: Lang; setLang: (lang: Lang) => void; t: T }
  const Context = createContext<Value | null>(null)

  function I18nProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState<Lang>(current)

    const setLang = useCallback((next: Lang) => {
      current = next
      try {
        localStorage.setItem(options.storageKey, next)
      } catch {
        // Not persisted; still applies to this session.
      }
      setLangState(next)
    }, [])

    useEffect(() => {
      document.documentElement.lang = lang
    }, [lang])

    const value = useMemo<Value>(() => ({ lang, setLang, t: (key, vars) => translate(key, vars, lang) }), [lang, setLang])
    return <Context.Provider value={value}>{children}</Context.Provider>
  }

  function useI18n(): Value {
    const value = useContext(Context)
    if (!value) throw new Error('useT/useLang used outside <I18nProvider>')
    return value
  }

  /** Translate a message key: `const t = useT(); t('common.save')`. Never call at module scope. */
  const useT = (): T => useI18n().t
  const useLang = (): { lang: Lang; setLang: (lang: Lang) => void } => {
    const { lang, setLang } = useI18n()
    return { lang, setLang }
  }

  return { I18nProvider, useT, useLang, translate, getLang }
}

/** A small language picker (English / Bahasa Indonesia). `label` is its accessible name. */
export function LanguageSelect({ lang, setLang, label, className = '' }: { lang: Lang; setLang: (lang: Lang) => void; label: string; className?: string }) {
  return (
    <label className={className}>
      <span className="sr-only">{label}</span>
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value as Lang)}
        className="h-9 rounded-[var(--mv-radius-sm)] border border-[var(--mv-border)] bg-[var(--mv-surface)] px-2 text-sm font-medium text-[var(--mv-fg-muted)] outline-none hover:bg-[var(--mv-grey-100)] focus:border-[var(--mv-blue)] focus:shadow-[var(--mv-ring)]"
      >
        {LANGS.map((l) => (
          <option key={l} value={l}>
            {LANG_NAMES[l]}
          </option>
        ))}
      </select>
    </label>
  )
}
